import { useRef, useState } from "react";
import { Box, BoxProps, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { ISalesContentData } from "src/api/server/wf-detail.types";
import { IconPlay } from "src/components/icons-mui/play";
import { ResponsiveImage } from "src/components-mui/base/responsive-image/responsive-image";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";

const SALES_SECTION_TEST_ID = "sales-section";
const VIDEO_CONTAINER_TEST_ID = "video-container";

export interface ISalesSectionProps extends BoxProps {
    data: ISalesContentData;
}

export const SalesSection = ({ data, ...boxProps }: ISalesSectionProps) => {
    const { t } = useTranslation("common");
    const [isPreviewImageVisible, setPreviewImageVisible] = useState(true);
    const playerRef = useRef<HTMLIFrameElement>(null);

    return (
        <Box component="section" {...boxProps} data-test-id={SALES_SECTION_TEST_ID}>
            <Stack direction="row" sx={{ alignItems: "center", mb: 3 }}>
                <WfHeading component="h2" fontSize="2xl">
                    {data.title}
                </WfHeading>
            </Stack>
            {data?.description && <SimpleRichText sx={{ mb: 5 }} fontSize="md" text={data.description} />}
            {data?.video && (
                <Box
                    data-test-id={VIDEO_CONTAINER_TEST_ID}
                    sx={{
                        position: "relative",
                        flexShrink: 0,
                        width: { xs: "100%", sm: 670, md: 604, lg: 767 },
                        aspectRatio: "16 / 9",
                        backgroundColor: "gray.800",
                        cursor: "pointer",
                    }}
                >
                    {isPreviewImageVisible && data.video.previewImage?.url ? (
                        <>
                            <ResponsiveImage
                                srcSet={[
                                    { queryParams: { w: 312, h: 176, fit: "crop" } },
                                    { queryParams: { w: 670, h: 378, fit: "crop" } },
                                    { queryParams: { w: 604, h: 341, fit: "crop" } },
                                    { queryParams: { w: 767, h: 432, fit: "crop" } },
                                ]}
                                baseUrl={data.video.previewImage.url}
                                alt={data.video.previewImage?.alt}
                                sx={{ position: "absolute", borderRadius: "4px", width: "100%" }}
                            />
                            <Stack
                                onClick={() => setPreviewImageVisible(false)}
                                role="button"
                                aria-label={t("click-to-play-video")}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    "&:hover": { backgroundColor: "whiteAlpha.300" },
                                }}
                            >
                                <IconPlay sx={{ fontSize: 80 }} />
                            </Stack>
                        </>
                    ) : (
                        <Box
                            component="iframe"
                            ref={playerRef}
                            src={data.video.url}
                            title={data.video.alt ?? ""}
                            allow="autoplay; fullscreen; encrypted-media"
                            sx={{ width: "100%", height: "100%", border: 0 }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};
